import React, { useContext, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { HiDotsVertical } from 'react-icons/hi';
import Select from 'react-select';
import { HSpace, VSpace } from '@/components/spacing';
import { Container } from '@/features/details/sessions-type-chip';
import { EditorContext } from '@/features/editor/context/editor-context';
import { countryCodeToFlag, commonCountryCodes } from '@/utils/country-codes';
import { Spinner } from '@/Spinner';
import {
  ActiveMarket,
  useStoreSettingsApi,
} from '@/webapi/use-store-settings-api';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

const LONG_LOADING_TIMEOUT = 5000;

export const PREVIEW_ACTIONS_POPOVER_ID = `preview-actions-popover`;

export function PreviewActions() {
  const { getGeneralStoreSettings } = useStoreSettingsApi();
  const isSpaIntegration = useFeatureBit(FeatureBit.SPA_INTEGRATION);
  const {
    devicePreview: {
      addCartItem,
      removeAllCartItems,
      sendChangeLocale,
      editorState,
    },
    deviceNavigation: { previewUrl },
  } = useContext(EditorContext);
  const [open, setOpen] = useState(false);
  const [markets, setMarkets] = useState<ActiveMarket[]>([]);
  const selectedMarket = editorState.currentLocale || null;
  const [loadingAction, setLoadingAction] = useState<null | 'add' | 'remove'>(
    null,
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await getGeneralStoreSettings();
        const active = resp?.inner?.activeMarkets ?? [];
        if (!mounted) return;
        if (active.length > 0) {
          setMarkets(active);
        } else {
          setMarkets(
            commonCountryCodes.map((c) => ({ key: c.key, name: c.name })),
          );
        }
      } catch {
        setMarkets(
          commonCountryCodes.map((c) => ({ key: c.key, name: c.name })),
        );
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const clear = () => setLoadingAction(null);
    window.addEventListener(`vsly-cart-action-done`, clear);
    return () => window.removeEventListener(`vsly-cart-action-done`, clear);
  }, []);

  const marketOptions = useMemo(
    () =>
      markets.map((m) => ({
        value: m.key,
        label: `${countryCodeToFlag(m.key)} ${m.name}`,
      })),
    [markets],
  );

  const watchLongLoading = () => {
    setTimeout(() => {
      setLoadingAction(null);
    }, LONG_LOADING_TIMEOUT);
  };

  const onAddCartItem = () => {
    setLoadingAction(`add`);
    addCartItem();
    watchLongLoading();
  };

  const onRemoveAllCartItems = () => {
    setLoadingAction(`remove`);
    removeAllCartItems();
    watchLongLoading();
  };

  return (
    <Popover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      containerStyle={{ zIndex: `29` }}
      positions={[`bottom`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={10}
          arrowStyle={{ margin: `1px 1px 1px 3px` }}
        >
          <PopoverContainer>
            <Header>SIMULATOR ACTIONS</Header>
            <Divider />
            <ActionItem
              $loading={loadingAction === `add`}
              onClick={onAddCartItem}
            >
              <span>Add a Cart Item</span>
              {loadingAction === `add` && (
                <Spinner mt="0px" size={18} border={2} relative color="#555" />
              )}
            </ActionItem>
            <Divider />
            <ActionItem
              $loading={loadingAction === `remove`}
              onClick={onRemoveAllCartItems}
            >
              <span>Remove all Cart Items</span>
              {loadingAction === `remove` && (
                <Spinner mt="0px" size={18} border={2} relative color="#555" />
              )}
            </ActionItem>

            {!isSpaIntegration && (
              <>
                <Divider />
                <VSpace value={0.5} />
                <Header>MARKET</Header>

                <Select
                  instanceId="preview-market-select"
                  options={marketOptions}
                  placeholder="Choose Market"
                  isDisabled={marketOptions.length <= 1}
                  value={
                    selectedMarket
                      ? marketOptions.find((o) => o.value === selectedMarket)
                      : null
                  }
                  onChange={(option) => {
                    if (!option) return;
                    sendChangeLocale(option.value, previewUrl);
                  }}
                  styles={marketSelectStyles}
                />
              </>
            )}
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container id={PREVIEW_ACTIONS_POPOVER_ID}>
        <HSpace />
        <ChipWrap onClick={() => setOpen(!open)}>
          <HiDotsVertical />
        </ChipWrap>
      </Container>
    </Popover>
  );
}

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 1.6rem 0px 0.8rem 0px;
  max-width: 250px;
  border-radius: 18px;
  width: 250px;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
`;

const ChipWrap = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  background: white;

  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', serif;
  user-select: none;

  color: #545454;
  border-radius: 2rem;
  padding: 1rem;

  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.09);

  :active {
    opacity: 1;
  }
`;

const Header = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #9f9f9f;
  padding: 0px 1.6rem;
  margin: 0.2rem 0rem 0.6rem 0.3rem;
`;

const ActionItem = styled.div<{ $loading?: boolean }>`
  font-size: 1.3rem;
  font-weight: 500;
  color: #000;
  padding: 1rem 1.6rem;
  cursor: ${({ $loading }) => ($loading ? `default` : `pointer`)};
  white-space: nowrap;

  display: flex;
  align-items: center;
  justify-content: space-between;

  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  pointer-events: ${({ $loading }) => ($loading ? `none` : `auto`)};
  transition: opacity 180ms ease;

  &:hover {
    opacity: ${({ $loading }) => ($loading ? 0.5 : 0.7)};
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #dcdcdc;
  opacity: 0.65;
  margin: 0.5rem 1.6rem 0.5rem 1.6rem;
`;

const marketSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: `8px`,
    border: `none`,
    boxShadow: `none`,
    padding: `2px 6px`,
    minHeight: `42px`,
    fontSize: `1.5rem`,
    fontWeight: 500,
    backgroundColor: `white`,
    opacity: 1,
    cursor: state.isDisabled ? `default` : `pointer`,
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    opacity: state.isDisabled ? 0.5 : 1,
  }),
  indicatorSeparator: () => ({
    display: `none`,
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: `0px`,
  }),
  singleValue: (base: any) => ({
    ...base,
    color: `#000`,
    opacity: 1,
    marginLeft: `10px`,
  }),
  input: (base: any) => ({
    ...base,
    marginLeft: `8px`,
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: `8px`,
    marginTop: `6px`,
    overflow: `hidden`,
    border: `1px solid rgba(0,0,0,0.08)`,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: `220px`,
    padding: 0,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: `1.5rem`,
    padding: `10px 12px`,
    cursor: `pointer`,
    background: state.isSelected ? `#f3f3f3` : `white`,
    fontWeight: state.isSelected ? 700 : 500,
    color: `#000`,
    ':hover': {
      background: `#f3f3f3`,
    },
  }),
  placeholder: (base: any) => ({
    ...base,
    color: `#000`,
    opacity: 0.7,
    marginLeft: `10px`,
  }),
};
